import React, { Component } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { userActions, hairdresserActions } from '../../_actions';
import AuthLayout from './AuthLayout';
import { currentAppleSignInState, clearAppleSignInState } from '../../_utils/appleSignIn';
import './auth.css';

// Landing page for Sign in with Apple. The v2 API has already verified the
// sign-in with Apple and sends the browser here with ONLY a one-time code
// (?code=…, or ?error=…). We trade { code, state } at v2 user/socialLogin; the
// server decides which account that is from its own record. If Apple gave us
// a private-relay address and no account is linked yet, the server asks for
// the user's real email (needsEmail) and we post it with the same code.
const APPLE_ERRORS = {
  cancelled: 'Apple sign-in was cancelled.',
  state: 'Apple sign-in could not be verified. Please try again.',
  apple: 'Apple sign-in failed. Please try again.'
};

class AppleRedirect extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      error: '',
      isLoading: true,
      // Private email handling
      showEmailForm: false,
      appleCode: '',
      appleState: '',
      privateEmail: '',
      email: '',
      first_name: '',
      last_name: '',
      emailError: '',
      fNameError: '',
      lNameError: ''
    };
    
    this.handleAppleResponse = this.handleAppleResponse.bind(this);
    this.checkApplePrivateEmail = this.checkApplePrivateEmail.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handlePrivateEmailSubmit = this.handlePrivateEmailSubmit.bind(this);
    this.validateEmail = this.validateEmail.bind(this);
  }

  componentDidMount() {
    const params = queryString.parse(this.props.location.search);
    // The code is single use, but keep it out of the address bar and history.
    if (params.code || params.error) {
      try { window.history.replaceState(null, '', this.props.location.pathname); } catch (e) { /* ignore */ }
    }

    if (params.error) {
      clearAppleSignInState();
      this.setState({ isLoading: false, error: APPLE_ERRORS[params.error] || APPLE_ERRORS.apple });
      return;
    }
    if (!params.code) {
      this.setState({ isLoading: false, error: 'No sign-in received from Apple. Please try again.' });
      return;
    }

    const appleState = currentAppleSignInState();
    this.setState({ appleCode: params.code, appleState: appleState });
    this.props.dispatch(hairdresserActions.dairyFilterData([]));
    this.props.dispatch(userActions.socialLogin({
      code: params.code,
      state: appleState,
      deviceToken: 'web',
      deviceTypeId: 2
    }))
      .then(this.handleAppleResponse)
      .catch((error) => {
        console.error('Apple login error:', error);
        this.setState({ isLoading: false, error: 'An error occurred. Please try again.' });
      });
  }

  handleAppleResponse(response) {
    const body = response && response.data;
    if (body && body.errorCode === 0 && body.data && body.data.needsEmail) {
      // Apple only gave us a private relay address: ask for their real one.
      this.setState({
        isLoading: false,
        showEmailForm: true,
        privateEmail: body.data.privateEmail || '',
        first_name: body.data.first_name || '',
        last_name: body.data.last_name || ''
      });
      return;
    }
    if (body && body.errorCode === 0 && body.data && body.data.tokenInfo) {
      clearAppleSignInState();
      this.handleSuccessfulLogin(response);
      return;
    }
    this.setState({
      isLoading: false,
      error: (body && body.messages) || 'Login failed. Please try again.'
    });
  }

  checkApplePrivateEmail(email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@privaterelay\.appleid\.com$/;
    return emailRegex.test(email);
  }

  validateEmail(email) {
    const pattern = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(email);
  }

  handleSuccessfulLogin(response) {
    // Check if user is a professional with a salon
    if (response.data.data.userInfo && 
        response.data.data.userInfo.hairdresser && 
        response.data.data.userInfo.hairdresser.salon) {
      // Professional login - redirect to diary
      const salonId = response.data.data.userInfo.hairdresser.salon._id;
      localStorage.setItem('salonId', salonId);
      localStorage.setItem('defaultRedirectTo', response.data.data.userInfo.defaultRedirectTo);
      localStorage.setItem('isShow', response.data.data.userInfo.hairdresser.salon.isShow);
      window.location.href = `/diary/${salonId}`;
    } else {
      // Client login - redirect to dashboard/account
      this.props.history.push('/dashboard/account');
    }
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
      [`${name}Error`]: ''
    });
  }

  handlePrivateEmailSubmit(e) {
    e.preventDefault();
    
    const { email, first_name, last_name } = this.state;
    const self = this;
    
    // Validation
    let hasError = false;
    
    if (!first_name.trim()) {
      this.setState({ fNameError: 'Please enter your first name' });
      hasError = true;
    }
    
    if (!last_name.trim()) {
      this.setState({ lNameError: 'Please enter your last name' });
      hasError = true;
    }
    
    if (!email.trim()) {
      this.setState({ emailError: 'Please enter your email address' });
      hasError = true;
    } else if (!this.validateEmail(email)) {
      this.setState({ emailError: 'Please enter a valid email address' });
      hasError = true;
    } else if (this.checkApplePrivateEmail(email)) {
      this.setState({ emailError: 'Please enter your real email address, not an Apple private relay email' });
      hasError = true;
    }
    
    if (hasError) return;
    
    this.setState({ isLoading: true, error: '' });

    this.props.dispatch(hairdresserActions.dairyFilterData([]));
    this.props.dispatch(userActions.socialLogin({
      code: this.state.appleCode,
      state: this.state.appleState,
      email: email.trim(),
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      deviceToken: 'web',
      deviceTypeId: 2
    }))
      .then(function(response) {
        const body = response && response.data;
        if (body && body.errorCode === 0 && body.data && body.data.tokenInfo) {
          clearAppleSignInState();
          self.handleSuccessfulLogin(response);
        } else {
          self.setState({
            isLoading: false,
            error: (body && body.messages) || 'Login failed. Please try again.'
          });
        }
      })
      .catch(function(error) {
        console.error('Apple login error:', error);
        self.setState({
          isLoading: false,
          error: 'An error occurred. Please try again.'
        });
      });
  }

  render() {
    const { isLoading, error, showEmailForm, email, first_name, last_name, emailError, fNameError, lNameError } = this.state;

    return (
      <AuthLayout>
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-logo">
              <img
                src={require('../../assets/images/loginLogo.png')}
                alt="Beu Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            {showEmailForm ? (
              <>
                <h1 className="auth-title">Complete Your Profile</h1>
                <p className="auth-subtitle">
                  To protect your privacy, Apple uses a private relay email. 
                  Please enter your real email address so we can contact you about your bookings.
                </p>
              </>
            ) : (
              <>
                <h1 className="auth-title">Signing In with Apple</h1>
                <p className="auth-subtitle">Please wait while we complete your sign in...</p>
              </>
            )}
          </div>

          <div className="auth-card-body">
            {error && (
              <div className="auth-error auth-mb-md">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {isLoading && !showEmailForm && (
              <div className="auth-loading">
                <div className="auth-spinner"></div>
                <p>Processing your Apple Sign-In...</p>
              </div>
            )}

            {showEmailForm && (
              <form className="auth-form" onSubmit={this.handlePrivateEmailSubmit}>
                <div className="auth-form-row">
                  <div className="auth-form-field">
                    <label className="auth-label" htmlFor="first_name">First Name</label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      className={`auth-input ${fNameError ? 'error' : ''}`}
                      placeholder="Enter your first name"
                      value={first_name}
                      onChange={this.handleChange}
                      autoFocus
                    />
                    {fNameError && (
                      <div className="auth-error">
                        <span>⚠️</span>
                        <span>{fNameError}</span>
                      </div>
                    )}
                  </div>

                  <div className="auth-form-field">
                    <label className="auth-label" htmlFor="last_name">Last Name</label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      className={`auth-input ${lNameError ? 'error' : ''}`}
                      placeholder="Enter your last name"
                      value={last_name}
                      onChange={this.handleChange}
                    />
                    {lNameError && (
                      <div className="auth-error">
                        <span>⚠️</span>
                        <span>{lNameError}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="auth-form-field">
                  <label className="auth-label" htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`auth-input ${emailError ? 'error' : ''}`}
                    placeholder="Enter your real email address"
                    value={email}
                    onChange={this.handleChange}
                    autoComplete="email"
                  />
                  {emailError && (
                    <div className="auth-error">
                      <span>⚠️</span>
                      <span>{emailError}</span>
                    </div>
                  )}
                  <p className="auth-hint" style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-muted)' }}>
                    We need your real email to send booking confirmations and reminders.
                  </p>
                </div>

                <button 
                  type="submit" 
                  className={`auth-button auth-button-primary ${isLoading ? 'auth-button-loading' : ''}`}
                  disabled={isLoading}
                >
                  Continue
                </button>
              </form>
            )}

            {!isLoading && !showEmailForm && error && (
              <button 
                className="auth-button auth-button-primary"
                onClick={() => this.props.history.push('/login')}
              >
                Back to Login
              </button>
            )}
          </div>
        </div>
      </AuthLayout>
    );
  }
}

function mapStateToProps(state) {
  return {};
}

export default connect(mapStateToProps)(AppleRedirect);